import type { Category, Locale, PostSummary } from '@ai-radar/shared';
import { apiUrl } from './site';

/**
 * Server-side reads from the NestJS API.
 *
 * Every fetch degrades rather than throws: the API is a separate process
 * and a dead one should leave the site rendering an empty state, not a
 * 500. Revalidation is short because the pipeline runs every 2 hours.
 */
async function get<T>(path: string, fallback: T, revalidate = 300): Promise<T> {
  try {
    const response = await fetch(`${apiUrl()}${path}`, {
      next: { revalidate },
      signal: AbortSignal.timeout(6000),
    });
    if (!response.ok) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

export interface HomeData {
  date: string;
  top: PostSummary[];
  hero: PostSummary | null;
  important: PostSummary[];
  tools: PostSummary[];
  models: PostSummary[];
  updates: PostSummary[];
  pricing: PostSummary[];
  trending: PostSummary[];
  worthTrying: PostSummary[];
  counts: Partial<Record<Category, number>>;
  totalPublished: number;
}

const EMPTY_HOME: HomeData = {
  date: new Date().toISOString(),
  top: [],
  hero: null,
  important: [],
  tools: [],
  models: [],
  updates: [],
  pricing: [],
  trending: [],
  worthTrying: [],
  counts: {},
  totalPublished: 0,
};

export const getHome = (locale: Locale) =>
  get<HomeData>(`/public/home?locale=${locale}`, EMPTY_HOME);

export interface FullPost extends PostSummary {
  whatChanged: string;
  whoShouldCare: string[];
  worthTryingNote: string | null;
  toolName: string | null;
  companySlug: string | null;
  sourcePublishedAt: string | null;
  confidence: number;
  seoTitle: string | null;
  seoDescription: string | null;
  tags: string[];
}

export const getPost = (slug: string, locale: Locale) =>
  get<{ post: FullPost; related: PostSummary[]; availableLocales: Locale[] } | null>(
    `/public/post/${slug}?locale=${locale}`,
    null,
  );

export const getCategory = (category: string, locale: Locale, limit = 30) =>
  get<{ category: string; posts: PostSummary[] }>(
    `/public/category/${category}?locale=${locale}&limit=${limit}`,
    { category, posts: [] },
  );

export const getCompany = (slug: string, locale: Locale) =>
  get<{ company: { id: number; name: string; slug: string }; posts: PostSummary[] } | null>(
    `/public/company/${slug}?locale=${locale}`,
    null,
  );

export interface SitemapData {
  posts: Array<{ slug: string; category: Category; published_at: string; updated_at: string }>;
  companies: Array<{ slug: string }>;
}

export const getSitemap = () =>
  get<SitemapData>('/public/sitemap', { posts: [], companies: [] }, 3600);

export interface AdSlotConfig {
  slot_key: string;
  enabled: number | boolean;
  provider: string | null;
  unit_id: string | null;
  min_width: number | null;
}

/**
 * Ad slot config.
 *
 * Short revalidate deliberately: at 3600s, enabling a slot in the admin
 * would not appear for an hour, which makes the toggle feel broken. The
 * response is tiny and the page around it is cached anyway.
 */
export const getAdSlot = (key: string) =>
  get<AdSlotConfig | null>(`/public/ad-slot/${key}`, null, 60);

// ------------------------------------------------- build-status page
// Used only by /status while the editor is not yet producing posts.

export interface PipelineRun {
  id: number;
  fetched: number;
  prefilter_kept: number;
  sources_checked: number;
  status: string;
}

export const getRuns = () => get<PipelineRun[]>('/pipeline/runs?limit=5', [], 60);

export const getRejections = () =>
  get<Array<{ reject_reason: string; count: number }>>('/pipeline/rejections?hours=720', [], 60);

export const getCandidates = () =>
  get<Array<{ id: number; title: string; url: string; source_name: string; published_at: string | null }>>(
    '/pipeline/candidates?limit=12',
    [],
    60,
  );

/** Bot username, or null when Telegram is not configured. */
export const getTelegramBot = () =>
  get<{ username: string | null }>('/public/telegram-bot', { username: null }, 3600);
